import React, { useEffect, useRef } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Play, Pause } from "lucide-react";
import { useContent } from "../context/ContentContext";
import { CASE_STUDY_BODIES } from "./CaseStudyBody";
import SEO from "./SEO";

/**
 * Per-slug SEO copy. Keeps titles ≤60 chars and descriptions in the 120–160
 * band so each capability detail page gets a unique snippet in search results.
 */
const CAP_SEO = {
  hybrid: {
    title: "Hybrid Production · Live Capture + AI Craft | SAMURAI",
    description:
      "SAMURAI's Hybrid Production choreographs AI, VFX and live-action into one seamless pipeline. See the Love Beauty & Planet before-and-after case study.",
  },
  ai: {
    title: "AI-Led Production · Fully Generated Film & Stills | SAMURAI",
    description:
      "Fully AI-generated production from prompt to final cut. Case study: Comfort — a complete campaign shot without a location, cast on set, or full crew.",
  },
  engines: {
    title: "Content Engines · Custom Generative Pipelines | SAMURAI",
    description:
      "Purpose-built content engines that turn a single idea into films, frames and every format a campaign needs. Case study: Google Pay Dilli, on-brand at scale.",
  },
};

/**
 * Sequential ordering of capabilities for the sticky Back/Next nav on the
 * detail page. Keep this in sync with the capabilities collection order.
 */
const CAP_ORDER = ["hybrid", "ai", "engines"];

/**
 * Case-study project labels per slug. Rendered inline in the CASE STUDY
 * kicker so every detail page identifies the featured brand.
 */
const CASE_STUDY_PROJECTS = {
  hybrid: "Love Beauty & Planet",
  ai: "Comfort",
  engines: "Google Pay",
};

/**
 * Static case-study content per capability slug. When a slug appears here we
 * skip the "Coming Soon" placeholder entirely and render the ordered media
 * blocks below the kicker + big title.
 */
const CASE_STUDIES = {
  hybrid: [
    {
      type: "video-pair",
      before: {
        src: "/capabilities/lbp-before.mp4",
        poster: "/capabilities/lbp-before-poster.png",
        label: "Before",
      },
      after: {
        src: "/capabilities/lbp-after.mp4",
        poster: "/capabilities/lbp-after-poster.png",
        label: "After",
      },
      caption:
        "For Love Beauty & Planet we captured the soul of the performance live and rendered the universe around it. Powered by proprietary tech, we choreograph AI, VFX, and live footage into single, seamless cinematic immersion.",
    },
  ],
  ai: [
    {
      type: "video-pair",
      chapters: 10,
      chapterDuration: 2.0,
      before: {
        src: "/capabilities/ai-before-seq.mp4",
        poster: "/capabilities/ai-before-seq.jpg",
        label: "Before",
      },
      after: {
        src: "/capabilities/ai-after-seq.mp4",
        poster: "/capabilities/ai-after-seq.jpg",
        label: "After",
      },
      caption:
        "Every asset was generated in AI. A short studio session for framing stills; motion directed through prompts. No location, no crew \u2014 full cinematic control.",
    },
  ],
  engines: [
    {
      type: "video",
      src: "/capabilities/gpay-dilli.mp4",
      poster: "/capabilities/gpay-poster.jpg",
      aspect: "16/9",
      caption:
        "Google needed illustration-led work across a huge spread of formats. A one-off wouldn't solve that. We built an engine that makes the film across formats.",
    },
  ],
};

/**
 * Two videos rendered side by side that play / pause together. Both muted,
 * no native controls; a centered overlay toggles playback for the pair.
 */
const VideoPair = ({ before, after, index, chapters, chapterDuration }) => {
  const beforeRef = useRef(null);
  const afterRef = useRef(null);
  const [playing, setPlaying] = React.useState(false);

  // Kick off synced autoplay on mount so the pair behaves like a real video.
  React.useEffect(() => {
    const b = beforeRef.current;
    const a = afterRef.current;
    if (!b || !a) return;
    b.muted = true;
    a.muted = true;
    const tryPlay = async () => {
      try {
        await Promise.all([b.play(), a.play()]);
        setPlaying(true);
      } catch (_) {
        // Autoplay blocked — leave paused; user can hit the center button.
      }
    };
    tryPlay();
  }, []);

  const toggle = async () => {
    const b = beforeRef.current;
    const a = afterRef.current;
    if (!b || !a) return;
    if (playing) {
      b.pause();
      a.pause();
      setPlaying(false);
      return;
    }
    // Ensure muted (browsers block autoplay of audible media on click too if
    // the element hasn't received a prior gesture on its origin).
    b.muted = true;
    a.muted = true;
    try {
      await Promise.all([b.play(), a.play()]);
      setPlaying(true);
    } catch (err) {
      // Fall back: try each one individually so at least one plays if the
      // other rejects (rare, but happens with some autoplay policies).
      try { await b.play(); } catch (_) {}
      try { await a.play(); } catch (_) {}
      setPlaying(true);
    }
  };

  // Sync state if either video ends or is paused externally.
  const onEnded = () => {
    setPlaying(false);
  };

  // Optional chapter-based seek — jumps both videos to the previous / next
  // chapter start. `chapters` = number of segments, `chapterDuration` = seconds
  // per segment (matches how the mp4 was authored).
  const seekChapter = (delta) => {
    const b = beforeRef.current;
    const a = afterRef.current;
    if (!b || !a || !chapters || !chapterDuration) return;
    const dur = chapters * chapterDuration;
    // Use the "before" clip as the source of truth for current position.
    const now = b.currentTime || 0;
    const cur = Math.floor(now / chapterDuration + 1e-3);
    const next = ((cur + delta) % chapters + chapters) % chapters;
    const t = Math.min(dur - 0.05, next * chapterDuration);
    try { b.currentTime = t; a.currentTime = t; } catch (_) {}
  };

  return (
    <div className="relative group" data-testid={`case-study-video-pair-${index}`}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
        <div className="relative flex-1 min-w-0">
          <span
            className="absolute top-3 left-3 z-10 text-[10px] tracking-[0.32em] uppercase text-ink/85 bg-canvas/60 backdrop-blur-sm border border-ink/20 rounded-full px-3 py-1"
            data-testid={`video-pair-label-${before.label.toLowerCase().replace(/\s+/g, "-")}`}
          >
            {before.label}
          </span>
          <video
            ref={beforeRef}
            src={before.src}
            poster={before.poster}
            muted
            loop
            autoPlay
            playsInline
            preload="auto"
            onEnded={onEnded}
            className="w-full aspect-video rounded-2xl bg-black shadow-2xl shadow-black/50 object-cover"
          />
        </div>
        <div className="relative flex-1 min-w-0">
          <span
            className="absolute top-3 left-3 z-10 text-[10px] tracking-[0.32em] uppercase text-ink/85 bg-canvas/60 backdrop-blur-sm border border-ink/20 rounded-full px-3 py-1"
            data-testid={`video-pair-label-${after.label.toLowerCase().replace(/\s+/g, "-")}`}
          >
            {after.label}
          </span>
          <video
            ref={afterRef}
            src={after.src}
            poster={after.poster}
            muted
            loop
            autoPlay
            playsInline
            preload="auto"
            onEnded={onEnded}
            className="w-full aspect-video rounded-2xl bg-black shadow-2xl shadow-black/50 object-cover"
          />
        </div>
      </div>
      <button
        type="button"
        onClick={toggle}
        aria-label={playing ? "Pause both" : "Play both"}
        data-testid="video-pair-toggle"
        className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-16 h-16 md:w-20 md:h-20 rounded-full bg-canvas/70 backdrop-blur border border-ink/25 hover:border-ink text-ink flex items-center justify-center transition-all opacity-0 group-hover:opacity-100 duration-500 group"
      >
        {playing ? (
          <Pause className="w-6 h-6 md:w-7 md:h-7" />
        ) : (
          <Play className="w-6 h-6 md:w-7 md:h-7 translate-x-[2px]" />
        )}
      </button>

      {chapters && chapterDuration && (
        <>
          <button
            type="button"
            onClick={() => seekChapter(-1)}
            aria-label="Previous shot"
            data-testid="video-pair-prev"
            className="absolute left-3 md:left-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-canvas/70 backdrop-blur border border-ink/25 text-ink hover:border-ink transition-all"
          >
            <ArrowLeft className="w-4 h-4 mx-auto" />
          </button>
          <button
            type="button"
            onClick={() => seekChapter(1)}
            aria-label="Next shot"
            data-testid="video-pair-next"
            className="absolute right-3 md:right-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-canvas/70 backdrop-blur border border-ink/25 text-ink hover:border-ink transition-all"
          >
            <ArrowRight className="w-4 h-4 mx-auto" />
          </button>
        </>
      )}
    </div>
  );
};

/**
 * Two 16:9 image slideshows rendered side by side (before / after) that
 * advance in sync. Auto-advances every 4500ms with a soft cross-fade; a
 * shared prev/next control set and dot indicators appear on hover and drive
 * both sides at the same time. `before.images` and `after.images` must be
 * equal-length arrays. A single caption below is optional.
 */
const SlideshowPair = ({ before, after, caption, index, autoAdvanceMs = 4500 }) => {
  const total = Math.min(before.images.length, after.images.length);
  const [i, setI] = React.useState(0);
  const [paused, setPaused] = React.useState(false);
  const wrapRef = useRef(null);

  React.useEffect(() => {
    if (paused || total <= 1) return undefined;
    const t = setInterval(() => setI((x) => (x + 1) % total), autoAdvanceMs);
    return () => clearInterval(t);
  }, [paused, total, autoAdvanceMs]);

  const go = (delta) => setI((x) => (x + delta + total) % total);

  const Side = ({ label, images }) => (
    <div className="relative flex-1 min-w-0 aspect-video rounded-2xl overflow-hidden bg-black shadow-2xl shadow-black/50">
      <span className="absolute top-3 left-3 z-20 text-[10px] tracking-[0.32em] uppercase text-ink/85 bg-canvas/60 backdrop-blur-sm border border-ink/20 rounded-full px-3 py-1">
        {label}
      </span>
      {images.map((src, idx) => (
        <img
          key={src + idx}
          src={src}
          alt={`${label} — slide ${idx + 1}`}
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-[900ms] ease-out ${
            idx === i ? "opacity-100" : "opacity-0"
          }`}
        />
      ))}
    </div>
  );

  return (
    <div
      ref={wrapRef}
      className="relative group"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      data-testid={`case-study-slideshow-pair-${index}`}
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
        <Side label={before.label} images={before.images} />
        <Side label={after.label} images={after.images} />
      </div>

      {/* Shared prev/next controls — appear on hover, drive both sides */}
      {total > 1 && (
        <>
          <button
            type="button"
            onClick={() => go(-1)}
            aria-label="Previous slides"
            data-testid="slideshow-prev"
            className="absolute left-3 md:left-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-canvas/70 backdrop-blur border border-ink/25 text-ink hover:border-ink transition-all opacity-0 group-hover:opacity-100 duration-500"
          >
            <ArrowLeft className="w-4 h-4 mx-auto" />
          </button>
          <button
            type="button"
            onClick={() => go(1)}
            aria-label="Next slides"
            data-testid="slideshow-next"
            className="absolute right-3 md:right-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-canvas/70 backdrop-blur border border-ink/25 text-ink hover:border-ink transition-all opacity-0 group-hover:opacity-100 duration-500"
          >
            <ArrowRight className="w-4 h-4 mx-auto" />
          </button>

          {/* Shared dots — always visible, softer at rest */}
          <div className="absolute left-1/2 -translate-x-1/2 bottom-4 flex items-center gap-2" data-testid="slideshow-dots">
            {Array.from({ length: total }).map((_, k) => (
              <button
                key={k}
                type="button"
                onClick={() => setI(k)}
                aria-label={`Go to slide ${k + 1}`}
                className={`h-1.5 rounded-full transition-all ${
                  k === i ? "w-6 bg-ink" : "w-1.5 bg-ink/40 hover:bg-ink/70"
                }`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};
const ImagePair = ({ before, after, alt, index }) => (
  <div
    className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6"
    data-testid={`case-study-image-pair-${index}`}
  >
    {[before, after].map((side, i) => (
      <div key={i} className="relative">
        <span className="absolute top-3 left-3 z-10 text-[10px] tracking-[0.32em] uppercase text-ink/85 bg-canvas/60 backdrop-blur-sm border border-ink/20 rounded-full px-3 py-1">
          {side.label}
        </span>
        <img
          src={side.src}
          alt={`${alt || "case study"} — ${side.label}`}
          className="w-full aspect-video object-cover rounded-2xl bg-black shadow-2xl shadow-black/40"
        />
      </div>
    ))}
  </div>
);

/**
 * Capability detail page.
 *
 * Route: /capabilities/:slug
 * Reads the capability from ContentContext (which is populated from
 * /api/content/all → capabilities collection). Renders a full-bleed
 * "Coming Soon." placeholder unless the CMS has flipped `is_coming_soon:false`.
 */
const CapabilityDetail = () => {
  const { slug } = useParams();
  const { CAPABILITIES } = useContent();
  const capability = CAPABILITIES.find((c) => c.id === slug);
  const name = capability?.title || "Capability";
  const caseStudy = CASE_STUDIES[slug];
  // Coming-soon by default. CMS stores as string or boolean — treat "false"/false as off.
  const rawFlag = capability?.is_coming_soon;
  const isComingSoon = !caseStudy && !(rawFlag === false || rawFlag === "false");

  useEffect(() => {
    const original = document.title;
    document.title = caseStudy ? `${name} — SAMURAI` : `${name} — Coming soon`;
    return () => {
      document.title = original;
    };
  }, [name, caseStudy]);

  if (!capability) {
    return (
      <div className="min-h-screen bg-canvas text-ink flex flex-col items-center justify-center px-6 text-center dark-block">
        <p className="text-[10px] tracking-[0.32em] uppercase text-ink/55 mb-4">
          Not found
        </p>
        <h1 className="font-heading font-black text-4xl md:text-6xl uppercase tracking-[-0.02em] mb-8">
          Unknown capability.
        </h1>
        <Link
          to="/capabilities"
          className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.32em] border border-ink/25 rounded-full px-5 py-2.5 hover:border-ink transition-colors"
          data-testid="capability-back"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Capabilities
        </Link>
      </div>
    );
  }

  return (
    <div
      className="dark-block bg-canvas text-ink min-h-screen flex flex-col relative overflow-hidden"
      data-testid={`capability-detail-${slug}`}
    >
      {CAP_SEO[slug] && (
        <SEO
          title={CAP_SEO[slug].title}
          description={CAP_SEO[slug].description}
          path={`/capabilities/${slug}`}
          type="article"
        />
      )}
      {/* soft grain / vignette to keep it cinematic */}
      <div className="absolute inset-0 noise-overlay pointer-events-none" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(244,239,230,0.06)_0%,transparent_60%)] pointer-events-none" />

      {/* Sticky top-left back — dynamic destination based on capability order.
          - Hybrid (index 0) → /capabilities
          - AI-Led (index 1) → /capabilities/hybrid
          - Engines (index 2) → /capabilities/ai
      */}
      {(() => {
        const idx = CAP_ORDER.indexOf(slug);
        const backTo =
          idx <= 0 ? "/capabilities" : `/capabilities/${CAP_ORDER[idx - 1]}`;
        return (
          <Link
            to={backTo}
            aria-label="Back"
            data-testid="capability-back-sticky"
            className="fixed top-24 md:top-28 left-4 md:left-8 z-40 group inline-flex items-center gap-2 h-10 pl-2 pr-4 rounded-full bg-canvas/70 backdrop-blur border border-ink/20 text-ink/85 hover:border-ink hover:text-ink transition-colors"
          >
            <span className="w-7 h-7 rounded-full border border-ink/30 flex items-center justify-center group-hover:border-ink transition-colors">
              <ArrowLeft className="w-3.5 h-3.5" />
            </span>
            <span className="text-[10px] tracking-[0.28em] uppercase">Back</span>
          </Link>
        );
      })()}

      {/* Sticky top-right next — mirror of Back, navigates to the next capability */}
      {(() => {
        const idx = CAP_ORDER.indexOf(slug);
        if (idx === -1) return null;
        const nextSlug = CAP_ORDER[(idx + 1) % CAP_ORDER.length];
        return (
          <Link
            to={`/capabilities/${nextSlug}`}
            aria-label="Next capability"
            data-testid="capability-next-sticky"
            className="fixed top-24 md:top-28 right-4 md:right-8 z-40 group inline-flex items-center gap-2 h-10 pl-4 pr-2 rounded-full bg-canvas/70 backdrop-blur border border-ink/20 text-ink/85 hover:border-ink hover:text-ink transition-colors"
          >
            <span className="text-[10px] tracking-[0.28em] uppercase">Next</span>
            <span className="w-7 h-7 rounded-full border border-ink/30 flex items-center justify-center group-hover:border-ink transition-colors">
              <ArrowRight className="w-3.5 h-3.5" />
            </span>
          </Link>
        );
      })()}

      {caseStudy ? (
        <div className="relative mx-auto px-6 md:px-10 w-full max-w-6xl pt-32 pb-24">
          <h1
            className="font-heading font-black uppercase leading-[0.95] tracking-[-0.03em] text-[28px] sm:text-[36px] md:text-[48px] lg:text-[56px] text-center mb-14 md:mb-20"
            data-testid="capability-case-headline"
          >
            {capability.title}
          </h1>

          <p
            className="text-[10px] md:text-[11px] tracking-[0.4em] uppercase text-ink/55 mb-6 text-center"
            data-testid="case-study-kicker"
          >
            Case Study{CASE_STUDY_PROJECTS[slug] ? ` \u2014 ${CASE_STUDY_PROJECTS[slug]}` : ""}
          </p>

          <div className="space-y-20 md:space-y-28">
            {caseStudy.map((block, i) => {
              let media;
              if (block.type === "video-pair") {
                media = (
                  <VideoPair
                    before={block.before}
                    after={block.after}
                    index={i}
                    chapters={block.chapters}
                    chapterDuration={block.chapterDuration}
                  />
                );
              } else if (block.type === "slideshow-pair") {
                media = (
                  <SlideshowPair
                    before={block.before}
                    after={block.after}
                    index={i}
                    autoAdvanceMs={block.autoAdvanceMs}
                  />
                );
              } else if (block.type === "image-pair") {
                media = (
                  <ImagePair
                    before={block.before}
                    after={block.after}
                    alt={capability.title}
                    index={i}
                  />
                );
              } else if (block.type === "video") {
                const isLandscape = block.aspect === "16/9";
                media = (
                  <video
                    src={block.src}
                    poster={block.poster}
                    controls
                    playsInline
                    preload="metadata"
                    className={
                      isLandscape
                        ? "mx-auto block w-full aspect-video rounded-2xl bg-black shadow-2xl shadow-black/50 object-contain"
                        : "mx-auto block w-full md:max-w-[560px] aspect-[4/5] rounded-2xl bg-black shadow-2xl shadow-black/50 object-contain"
                    }
                    data-testid={`case-study-video-${i}`}
                  />
                );
              } else {
                media = (
                  <img
                    src={block.src}
                    alt={block.alt || capability.title}
                    className="mx-auto block w-full md:w-[62%] rounded-2xl object-contain bg-black"
                    data-testid={`case-study-image-${i}`}
                  />
                );
              }
              return (
                <figure key={i} className="space-y-6" data-testid={`case-study-block-${i}`}>
                  {media}
                  {block.caption && (
                    <figcaption
                      className="mx-auto max-w-3xl text-center text-sm md:text-base text-ink/85 leading-[1.6] font-light tracking-[-0.005em]"
                      style={{
                        fontFamily:
                          "'Geist', 'Inter', ui-sans-serif, system-ui, sans-serif",
                      }}
                    >
                      {block.caption}
                    </figcaption>
                  )}
                </figure>
              );
            })}
          </div>

          {/* Rich long-scroll editorial body (per slug) */}
          {CASE_STUDY_BODIES[slug] &&
            (() => {
              const Body = CASE_STUDY_BODIES[slug];
              return <Body />;
            })()}

          <div className="mt-20 text-center">
            <Link
              to="/capabilities"
              className="inline-flex items-center gap-3 text-[11px] uppercase tracking-[0.32em] border border-ink/25 rounded-full px-6 py-3 hover:border-ink transition-colors"
              data-testid="capability-back"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Capabilities
            </Link>
          </div>
        </div>
      ) : (
        <div className="relative mx-auto px-6 md:px-10 w-full max-w-5xl text-center pt-32 pb-20 my-auto">
          <p className="text-[10px] md:text-[11px] tracking-[0.4em] uppercase text-ink/55 mb-6" data-testid="capability-name-kicker">
            Capability {capability.number} · {capability.title}
          </p>

          {isComingSoon ? (
            <>
              <h1
                className="font-heading font-black uppercase leading-[0.95] tracking-[-0.03em] text-[36px] sm:text-[52px] md:text-[72px] lg:text-[84px]"
                data-testid="capability-coming-soon-headline"
              >
                Coming{" "}
                <span className="italic font-serif font-normal text-ink/70 normal-case">
                  soon
                </span>
              </h1>

              <p className="mt-8 max-w-md mx-auto text-sm md:text-base text-ink/70 font-light leading-relaxed" style={{ fontFamily: "'Geist', 'Inter', ui-sans-serif, system-ui, sans-serif" }}>
                We&apos;re crafting a longer story for {capability.title}. Sit tight — the
                full walkthrough drops shortly.
              </p>
            </>
          ) : (
            <div className="text-left space-y-10">
              <h1 className="font-heading font-black uppercase leading-[0.95] tracking-[-0.03em] text-[42px] md:text-[72px]">
                {capability.title}
              </h1>
              {capability.subtitle && (
                <p className="font-serif italic text-xl md:text-3xl text-ink/85 leading-snug">
                  {capability.subtitle}
                </p>
              )}
              {capability.detail_image && (
                <img
                  src={capability.detail_image}
                  alt={capability.title}
                  className="w-full rounded-2xl object-contain"
                />
              )}
              {capability.detail_body && (
                <p className="text-base md:text-lg text-ink/80 leading-relaxed whitespace-pre-line" style={{ fontFamily: "'Geist', 'Inter', ui-sans-serif, system-ui, sans-serif" }}>
                  {capability.detail_body}
                </p>
              )}
            </div>
          )}

          <div className="mt-14">
            <Link
              to="/capabilities"
              className="inline-flex items-center gap-3 text-[11px] uppercase tracking-[0.32em] border border-ink/25 rounded-full px-6 py-3 hover:border-ink transition-colors"
              data-testid="capability-back"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Capabilities
            </Link>
          </div>
        </div>
      )}
    </div>
  );
};

export default CapabilityDetail;
